/*
 *  -----------------------------------------------------------------------
 *    <copyright file="multi-select/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import { filterBy, FilterOperator } from '@progress/kendo-data-query';
import { MultiSelect, MultiSelectFilterChangeEvent } from '@progress/kendo-react-dropdowns';
import cn from 'clsx';
import React, { useEffect, useState } from 'react';
import CustomTooltip from '@components/core/custom-tooltip';
import MultiSelectNoResults from '../multi-select-no-results';
import '../styles.scss';
import { CustomMultiSelectProps } from '../multi-select.interface';
import { useBoolean } from 'hooks';
import { textToId } from '@utils/automation-test-helpers';

const CustomMultiSelect = ({
    items,
    onChange,
    label,
    value,
    customId,
    customClassName,
    popupClassName = 'width-300',
    placeholder,
    error,
    warning,
    allowCustom,
    disabled,
    addNewAction,
}: CustomMultiSelectProps) => {
    const [data, setData] = useState<any[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const showPlaceholder = useBoolean(true);
    const hideCursor = useBoolean(false);
    const isFocused = useBoolean(false);
    const [filter, setFilter] = useState('');

    const updateFilteredData = (filterValue: string, inputData: any[]) => {
        setFilteredData(
            filterBy(inputData.slice(), {
                value: filterValue,
                field: 'name',
                operator: FilterOperator.Contains,
            }),
        );
    };

    useEffect(() => {
        if (items?.length) setData(items);
    }, [items]);

    useEffect(() => {
        updateFilteredData(filter, data);
    }, [data]);

    useEffect(() => {
        if (showPlaceholder.value) hideCursor.setTrue();
        if (!value?.length || !showPlaceholder.value) hideCursor.setFalse();
    }, [showPlaceholder.value, data]);

    const filterChange = (event: MultiSelectFilterChangeEvent) => {
        updateFilteredData(event.filter.value, items);
        setFilter(event.filter.value);
    };

    const onAddNew = () => {
        setFilter('');
        if (addNewAction) addNewAction();
    };

    return (
        <FormControl
            variant="outlined"
            className={cn(customClassName, { error }, 'multi-select')}
            id={customId}
            data-testid={customId}
        >
            <span className={cn('multi-select-label', { focused: isFocused.value }, { error }, { disabled })}>
                {label}
            </span>
            <MultiSelect
                ref={(instance) => {
                    (window as any)[customId || 'multiselect'] = instance;
                }}
                allowCustom={!!allowCustom}
                onFocus={() => {
                    showPlaceholder.setFalse();
                    isFocused.setTrue();
                }}
                onBlur={() => {
                    showPlaceholder.setTrue();
                    isFocused.setFalse();
                }}
                disabled={!!disabled}
                data={filteredData}
                onChange={(event) => {
                    onChange(event.target.value);
                }}
                popupSettings={{ className: cn('multi-select-popup', popupClassName, { hideCursor }) }}
                value={value}
                placeholder={placeholder}
                filterable
                filter={filter}
                onFilterChange={filterChange}
                listNoDataRender={() =>
                    !allowCustom ? <MultiSelectNoResults addNewAction={addNewAction && onAddNew} /> : ''
                }
                itemRender={(_, itemProps) =>
                    items.length !== 0 ? (
                        <div
                            className="select-item"
                            onClick={() => {
                                const array = value || [];
                                onChange([...array, itemProps.dataItem]);
                                setFilter('');
                            }}
                            data-testid={`select-item-${textToId(itemProps.dataItem.name)}`}
                        >
                            <CustomTooltip
                                title={itemProps.dataItem.name}
                                key="tooltip-select"
                                classes={{
                                    tooltip: 'tooltip-in-select-comp',
                                    popper: 'always-visible',
                                }}
                                disableHoverListener={itemProps.dataItem.name.length <= 190 ?? true}
                                slotProps={{
                                    popper: { placement: 'top' },
                                }}
                            >
                                <span className="ellipsis-wrap">{itemProps.dataItem.name}</span>
                            </CustomTooltip>
                        </div>
                    ) : (
                        <MultiSelectNoResults addNewAction={addNewAction && onAddNew} />
                    )
                }
                tags={value.map((item, index) => ({ text: item.name, data: [item], index }))}
                tagRender={(tagData) => {
                    const tagDataItem = tagData.data[0];
                    return (
                        <div key={tagData.text || ''}>
                            <p className={cn('select-tag', { 'show-warning': tagDataItem?.showWarning })}>
                                <CustomTooltip
                                    title={tagData.text}
                                    key="tooltip-select"
                                    classes={{
                                        tooltip: 'tooltip-in-select-comp',
                                    }}
                                    disableHoverListener={tagData.text.length <= 70 ?? true}
                                >
                                    <span className="tag-text">{tagData.text || ''}</span>
                                </CustomTooltip>

                                <button
                                    type="button"
                                    className="remove-item-btn"
                                    onClick={(event) => {
                                        event.stopPropagation(); // It's preventing the dropdown from opening when we remove the item
                                        if (value.length === 1) {
                                            onChange([]);
                                        } else {
                                            const index = value.findIndex((item) => item.id === tagDataItem.id);
                                            value.splice(index, 1);
                                            onChange(value);
                                        }
                                    }}
                                    data-testid={`remove-item-${textToId(tagData.text.replace('- ', ''))}`}
                                >
                                    <FontAwesomeIcon
                                        icon={['fal', 'times']}
                                        className="fa-lg close-icon-cv"
                                        data-testid="close-dialog"
                                    />
                                </button>
                            </p>
                        </div>
                    );
                }}
            />
            {!!error && <p className="error-message">{error}</p>}
            {!!warning && <p className="warning-message">{warning}</p>}
        </FormControl>
    );
};

export default CustomMultiSelect;
